export const queryKeys = {
  partners: {
    all: ['partners'] as const,
    detail: (id: string) => ['partners', id] as const,
  },
  connections: {
    all: ['connections'] as const,
    detail: (id: string) => ['connections', id] as const,
    // Under `connections` so connection_status_changes (which invalidates connections.all)
    // reaches an open partner modal. Same reasoning as tasks.byPartner below.
    //
    // byPartner        -> GET /partners/{id}/connections (partner_management_api's whitelist
    //                     mapper: no pgp*Configured, no test/diagnostic fields)
    // byPartnerDetailed -> GET /connections?partnerId=... (canonical transformConnection: full
    //                     Connection, including the pgp flags Edit Task gates its PGP toggle on)
    //
    // These are two DIFFERENT SHAPES of the same logical thing, so they get two keys. Collapsing
    // them would resurrect the cache-aliasing bug that shipped for the task-execution reads --
    // whichever reader populated first would decide what the other one saw.
    byPartner: (partnerId: string) => ['connections', { partnerId }] as const,
    byPartnerDetailed: (partnerId: string) =>
      ['connections', 'detailed', { partnerId }] as const,
  },
  // DELIBERATELY OUTSIDE the ['tasks'] prefix. Read this before "tidying" it back in.
  //
  // Five call sites invalidate queryKeys.tasks.all (App.tsx's coalesced execution_updates /
  // task_status_changes flush, useTaskActions, and three in useTaskManagement), and
  // invalidateQueries refetches every ACTIVE observer under the prefix regardless of staleTime.
  //
  // Held files is a MODAL SNAPSHOT the operator makes a DELIVERY decision inside, and its
  // selection is derived (every deliverable row not explicitly deselected). Keyed under ['tasks'],
  // a background refetch appended newly-arrived rows ALREADY SELECTED, so Confirm could deliver a
  // file the operator never saw. staleTime: Infinity does not prevent that -- invalidateQueries
  // refetches an active observer regardless -- so only being outside the prefix fixes it.
  //
  // Note the contrast with tasks.executionPages below, which stays INSIDE the prefix on purpose:
  // there, going stale is itself the bug (observed on DEV 2026-08-12). The distinguishing question
  // is not "is a refetch expensive" but "does a refetch change something the operator is in the
  // middle of acting on".
  heldFiles: (id: string) => ['heldFiles', id] as const,

  // Also deliberately outside ['tasks'], for a different reason: this is REFERENCE data for a
  // search form's dropdown, not a live list.
  //
  // execution_updates -> [queryKeys.tasks.all] (wsInvalidationMap.ts) is the highest-volume topic
  // -- one broadcast per file -- and Transfer History held an active observer on that key purely to
  // populate its task dropdown. At NP's measured peak file rate that is ~2.09 GET /tasks per second
  // at ~423 KB uncompressed, ~633 MB per batch window PER OPEN TAB, and not one of those refetches
  // can change what the dropdown renders: an execution event cannot rename a task or move it to a
  // different partner. The pre-slate page fetched this list once in a mount effect, so the waste is
  // new to the TanStack conversion.
  //
  // A slightly stale name in a filter dropdown is the correct trade here; staleTime still refreshes
  // it on revisit, which is strictly fresher than what this page did before.
  taskOptions: ['taskOptions'] as const,

  // Task History's cursor-paginated read, deliberately OUTSIDE the ['tasks'] prefix.
  //
  // This is a decision with a real trade-off on both sides, so it is written down rather than left
  // to be re-derived. Under ['tasks'], every execution_updates flush (one broadcast per file,
  // coalesced to ~2/s) re-fetched EVERY page the operator had loaded, because TanStack refetches an
  // infinite query's pages as a set. That scales with scroll depth, not with the data: NP's largest
  // task is ~10,968 executions, so a fully-scrolled tab reached ~470 req/s and ~27 MB/s from one
  // browser, unbounded in pages.
  //
  // Being under the prefix was NOT arbitrary though -- it fixed a logged defect: the History tab sat
  // at 03:38 while the clock read 03:42 (DEV 2026-08-12) because nothing could refresh it. So the
  // freshness requirement is real and stays met, by a different mechanism: useTaskExecutionHistory
  // subscribes to execution_updates for its own task and raises `hasPendingUpdates`, and the History
  // tab shows a "New executions available - Refresh" cue. The operator decides when to pay for the
  // page walk. Live watching is what the Monitor tab is for.
  //
  // Chosen by Paul on 2026-08-25 over: refetch-page-1-only, leave-as-is, and capping retained pages.
  taskExecutionPages: (id: string, pageSize: number) =>
    ['taskExecutionPages', id, pageSize] as const,
  tasks: {
    all: ['tasks'] as const,
    detail: (id: string) => ['tasks', id] as const,
    // THUB-299: the two execution reads are DIFFERENT SHAPES and must never share a cache entry.
    //
    // They did, and it shipped: TaskList's plain useQuery and useTaskExecutionHistory's
    // useInfiniteQuery both built ['tasks', id, 'executions', 50] by hand (the hook's default
    // pageSize is 50). One writes a TaskExecution[], the other expects {pages, pageParams}, so
    // whichever wrote last, the infinite reader's `data.pages.flatMap(...)` hit undefined and the
    // History tab crashed with "Cannot read properties of undefined (reading 'length')".
    //
    // Distinct, named factories instead of hand-assembled suffixes: the shapes cannot alias, and
    // the difference is visible at the call site. recentExecutions stays under ['tasks'] because it
    // is one bounded request feeding the Overview counters, so refreshing it on a broadcast is free.
    //
    // The PAGED read moved out -- see queryKeys.taskExecutionPages below.
    recentExecutions: (id: string, limit: number) =>
      ['tasks', id, 'executions', 'recent', limit] as const,
    // THUB-299: tasks scoped to a parent. Deliberately under `tasks` and NOT under
    // connections/partners: these ARE task lists, and App.tsx maps task_status_changes and
    // execution_updates to queryKeys.tasks.all, so keying them here is what lets the central
    // coalesced invalidation reach an open details modal. That is exactly the reach THUB-182
    // had to hand-roll a WebSocket subscription to fake.
    byConnection: (connectionId: string) => ['tasks', { connectionId }] as const,
    byPartner: (partnerId: string) => ['tasks', { partnerId }] as const,
  },
  analytics: {
    system: ['analytics', 'system'] as const,
  },
  // THUB-299: file-audit reads. Deliberately NOT nested under `tasks`, because
  // `execution_updates` invalidates queryKeys.tasks.all on every file event and that would
  // refetch journeys nobody is looking at. The File Journey modal has its own refresh control,
  // which preserves the previous behaviour exactly.
  fileAudit: {
    journey: (fileId: string) => ['fileAudit', 'journey', fileId] as const,
  },
  // THUB-299: configuration audit trail. Two shapes because the viewer has two modes -- an
  // entity's own history, and a filtered global search. Filters belong IN the search key: each
  // filter combination is its own pagination sequence, so they must not share a cache entry.
  // THUB-299: a policy simulation is keyed by the policy TEXT, because that is exactly what the
  // result is a function of. Editing the policy is therefore a different cache entry, which is
  // what makes a slow result for the previous draft unable to be shown for the current one.
  // THUB-299: the caller's own role/identity. One entry -- it is always "me".
  // THUB-298: RBAC admin reads. Under their own namespace, not `auth`, because `auth.me` is the
  // caller's own identity while these are the tenant-wide mappings only an admin sees.
  rbac: {
    roleMappings: ['rbac', 'roleMappings'] as const,
    // The "full" read carries knownGroups alongside the mappings, so it is a different shape and
    // must not share the key above -- the exact aliasing THUB-299 crashed on.
    roleMappingsFull: ['rbac', 'roleMappings', 'full'] as const,
  },
  auth: {
    me: ['auth', 'me'] as const,
  },
  policy: {
    list: ['policy', 'list'] as const,
    simulation: (cedarPolicy: string) => ['policy', 'simulation', cedarPolicy] as const,
  },
  auditLog: {
    all: ['auditLog'] as const,
    entity: (entityType: string, entityId: string) =>
      ['auditLog', 'entity', entityType, entityId] as const,
    search: (action: string, entityType: string) =>
      ['auditLog', 'search', { action, entityType }] as const,
  },
};
